import { useFormState, useWatch } from 'react-hook-form';
import { SelectField } from '@mews-commander/form';
import {
    FormSwitchField,
    FormTextareaField,
    LocalizedPropertyObject,
    LocalizedText,
    FormControls,
} from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, Divider, IconName, Stack, Typography } from '@optimus-web/core';
import { MessageTypeServiceConfiguration } from '@mews-commander/data-access';
import { Option } from '@mews-commander/types';
import { TooltippedIcon } from '@mews-commander/ui';
import { getLanguageOptions } from '@mews-commander/legacy-entities/src/ducks/languages';
import { useCommanderSelector } from '@mews-commander/utils';
import { AfterStartFormValues } from './types';

interface AfterStartFormFieldsProps extends Pick<FormControls<AfterStartFormValues>, 'submit'> {
    messageTemplate: LocalizedPropertyObject | undefined;
    services?: MessageTypeServiceConfiguration[] | null;
}

export const AfterStartFormFields = ({ submit, messageTemplate, services }: AfterStartFormFieldsProps) => {
    const { dirtyFields } = useFormState();
    const languageCode = useWatch<AfterStartFormValues, 'languageCode'>({ name: 'languageCode' });
    const translatedTemplate = messageTemplate?.[languageCode] ?? '';
    const languageOptions = useCommanderSelector(getLanguageOptions);
    const isDirty = dirtyFields.isEnabled != null || dirtyFields.serviceIds != null;

    const servicesOptions: Option[] =
        services?.map((service) => ({
            value: service.id,
            label: service.names[languageCode] ?? '',
            disabled: false,
            secondaryText: '',
            inactive: false,
            indent: false,
        })) ?? [];

    return (
        <Stack vertical spacing="200">
            <Stack itemAlignment="center" distribution="space-between">
                <Typography textStyle="bodyLargeStrong" as="p">
                    <LocalizedText textKey={TextKey.AfterStartTabSwitchFieldLabel} />
                </Typography>
                <FormSwitchField label={null} name="isEnabled" />
            </Stack>
            <Divider />
            <Stack vertical spacing="100">
                <SelectField
                    label="Services"
                    multiselect
                    name="serviceIds"
                    options={servicesOptions}
                    preserveMultiSelectSearch
                    size="large"
                />
                <Typography textStyle="bodyMedium" as="p">
                    <LocalizedText textKey={TextKey.AfterStartTabServicesFieldInfoText} />
                </Typography>
            </Stack>
            <SelectField
                label={<LocalizedText textKey={TextKey.Language} />}
                name="languageCode"
                options={languageOptions}
                showEmptyValue={false}
                size="large"
            />

            <Stack vertical spacing="100">
                <Stack fill distribution="space-between">
                    <LocalizedText textKey={TextKey.Message} />
                    <TooltippedIcon
                        size="small"
                        value={IconName.Info}
                        tooltip={<LocalizedText textKey={TextKey.TextMessagingAfterStartMessageFieldInfoTooltip} />}
                    />
                </Stack>
                <FormTextareaField size="large" name="message" placeholder={translatedTemplate} readOnly />
            </Stack>

            <Stack alignment="end">
                <Button disabled={!isDirty} variant="primary" onClick={submit} size="medium" type="submit">
                    <LocalizedText textKey={TextKey.Save} />
                </Button>
            </Stack>
        </Stack>
    );
};
